import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import { GridCellParams } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import PromoSearchForm from '~/components/Common/Forms/PromoSearchForm';
import PromoBulkExpiryUpdate from '~/components/Promo/PromoBulkExpiryUpdate';
import PromoBulkOperations, { BulkOperation, BulkOperationRowStatus } from '~/components/Promo/PromoBulkOperations';
import PromoPage from '~/components/Promo/PromoPage';
import { usePromoCodes } from '~/components/Promo/hooks/usePromoCodes';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import { isPromoBrand } from '~/components/Promo/promoBrandUtils';
import { promoIdPath } from '~/components/Promo/promoUrls';

import { GetPromoCodeProps, expirePromo, getPromoCodes } from '~/services/PromoService';

const CHILD_PROMO_PAGE_SIZE = 10;

type PromoChildSearchResultsProps = {
  codeName: string;
};

export default function PromoChildSearchResults({ codeName }: PromoChildSearchResultsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [bulkOperation, setBulkOperation] = useState<BulkOperation | null>(null);
  const [bulkRowStatuses, setBulkRowStatuses] = useState<Record<string, BulkOperationRowStatus>>({});
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const initialFilters = useMemo<GetPromoCodeProps>(
    () => ({
      page: 1,
      limit: CHILD_PROMO_PAGE_SIZE,
      codeName,
      emailAddress: '',
      caseId: '',
      expiresAfter: '',
      noChildPromos: false,
      onlyChildPromos: true,
      excludeReferralPromos: false,
      deptTag: '',
      categoryTag: '',
      subCategoryTag: '',
      brand,
      dev: false,
    }),
    [brand, codeName],
  );
  const { promoCodes, isLoading, fetchData, filters, setFilters, total } = usePromoCodes({ initialFilters });
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  useEffect(() => {
    if (isExpanded) {
      fetchData(filters);
    }
  }, [fetchData, filters, isExpanded]);

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const nextFilters = { ...filters, page: page + 1, limit: pageSize };
      setFilters(nextFilters);
    },
    [filters, setFilters],
  );

  const searchChildren = useCallback(
    (searchFilters: GetPromoCodeProps) => {
      setFilters({
        ...searchFilters,
        page: 1,
        brand,
        codeName,
        onlyChildPromos: true,
      });
    },
    [brand, codeName, setFilters],
  );

  const expireChildPromo = useCallback(
    async (id: string) => {
      await expirePromo(id, brand);
      await fetchData(filters);
    },
    [brand, fetchData, filters],
  );

  const loadSearchPage = useCallback(
    async (page: number, batchSize: number) => {
      const response = await getPromoCodes({ ...filters, page, limit: batchSize });
      return response.result;
    },
    [filters],
  );

  const openPromo = useCallback((row: GridCellParams<App.PromoData>) => {
    // These cells render links or interactive status details, so their own
    // interactions must not also open the child promo.
    if (
      row.field === 'code_name' ||
      row.field === 'added_by' ||
      row.field === 'cloned_from_promo_id' ||
      row.field === 'expires_at' ||
      row.field === 'promo_toggles' ||
      row.field === 'debug'
    ) {
      return;
    }
    window.open(promoIdPath(row.row.id_promo_code), '_blank');
  }, []);

  return (
    <Accordion
      disableGutters
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      sx={{ width: '100%' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: 'action.hover',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <PageSubheader title="Child Codes" />
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <PromoSearchForm
            searchQuery={searchChildren}
            isLoading={isLoading}
            initialFilters={initialFilters}
            opts={{
              showPromoCodeName: false,
              showPromoTags: false,
              showNoChildPromos: false,
              showOnlyChildPromos: false,
              showDev: false,
              showCaseId: true,
              showEmailRestriction: true,
              showExcludeReferralPromos: true,
              showSearchAll: false,
              showClearAll: false,
              showSearchButtonOnOwnRow: true,
            }}
          />
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isLoading ? 'Loading child promo codes…' : `${total} child promo code${total === 1 ? '' : 's'} found`}
          </Typography>
          {promoCodes !== null && promoMeta && !isPromoMetaLoading && (
            <PromoPage
              promos={promoCodes}
              total={total}
              isLoading={isLoading}
              page={filters.page}
              sizePerPage={filters.limit}
              isDev={false}
              onPageChange={handlePageChange}
              onClick={openPromo}
              expirePromo={expireChildPromo}
              promoMeta={promoMeta}
              bulkOperation={bulkOperation}
              bulkRowStatuses={bulkRowStatuses}
              refined
              hideParentPromoId
            />
          )}
          {promoCodes !== null && promoMeta && !isPromoMetaLoading && isPromoBrand(brand) && (
            <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" sx={{ mt: 2 }}>
              <PromoBulkExpiryUpdate
                activeOperation={bulkOperation}
                currentPagePromos={promoCodes}
                totalSearchResults={total}
                isUnfilteredAllSearch={false}
                searchFilters={filters}
                loadSearchPage={loadSearchPage}
                onComplete={() => fetchData(filters)}
                onOperationChange={setBulkOperation}
                onStatusChange={setBulkRowStatuses}
              />
              <PromoBulkOperations
                activeOperation={bulkOperation}
                currentPagePromos={promoCodes}
                totalSearchResults={total}
                isUnfilteredAllSearch={false}
                searchFilters={filters}
                loadSearchPage={loadSearchPage}
                onComplete={() => fetchData(filters)}
                onOperationChange={setBulkOperation}
                onStatusChange={setBulkRowStatuses}
              />
            </Stack>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
